import { cn } from 'src/utils/cn'
import {
  ComponentProps,
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactNode,
} from 'react'
import Link, { LinkProps } from 'next/link'

const backgroundColors = {
  primary: 'bg-primary',
  secondary: 'bg-secondary',
}

const typographyVariants = {
  'body-small':
    'text-content-primary text-[14px] font-normal leading-[140%] tracking-normal text-center max-w-[800px] mx-auto',
  'body-xsmall':
    'text-content-primary text-[12px] font-normal leading-[140%] tracking-normal',
  'body-medium':
    'text-content-primary text-[16px] font-normal leading-[140%] tracking-normal text-center max-w-[800px] mx-auto',
  'heading-small':
    'text-content-primary text-[18px] font-bold leading-[130%] tracking-normal text-center',
  'heading-large':
    'text-content-primary text-[24px] lg:text-[32px] font-bold leading-[130%] tracking-normal text-center max-w-[800px] mx-auto',
  'label-overline':
    'text-content-secondary text-[14px] font-bold leading-[120%] tracking-[0.25em] text-center uppercase',
  'display-small':
    'text-content-primary text-[16px] lg:text-[20px] font-medium leading-[120%] tracking-[0em] text-center',
  'display-medium':
    'text-content-primary text-[20px] lg:text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-center',
  'display-large':
    'text-content-primary text-[28px] lg:text-[48px] font-medium leading-[120%] tracking-[-0.01em] text-center',
  'label-button-medium':
    'text-white text-[18px] font-bold leading-[120%] tracking-[0.02em] text-center',
  'label-medium':
    'text-content-primary text-[16px] font-medium leading-[120%] tracking-[0] text-center',
  'body-link-medium':
    'text-content-secondary text-[16px] font-normal leading-[140%] tracking-normal text-center max-w-[800px] mx-auto underline',
}

const CTAButtonVariants = {
  primary:
    'bg-content-accent rounded-lg hover:bg-content-accent-hover transition-colors duration-300 flex items-center justify-center',
}

type SectionVariant = keyof typeof backgroundColors
type TypographyVariant = keyof typeof typographyVariants
type CTAButtonVariant = keyof typeof CTAButtonVariants

interface SectionProps extends ComponentProps<'section'> {
  children?: ReactNode
  className?: string
  variant?: SectionVariant
}

interface TypographyProps extends HTMLAttributes<HTMLElement> {
  as?: ElementType
  variant?: TypographyVariant
}

interface CTAButtonProps extends LinkProps {
  variant?: CTAButtonVariant
  label: string
}

const SectionAlone = forwardRef<HTMLElement, SectionProps>(
  ({ children, className, variant = 'primary', ...props }, ref) => {
    const backgroundColor = backgroundColors[variant]

    return (
      <section
        ref={ref}
        className={cn('mx-auto px-5 py-12', backgroundColor)}
        {...props}
      >
        <div className={cn('max-w-304 mx-auto', className)}>{children}</div>
      </section>
    )
  }
)

SectionAlone.displayName = 'Section'

const Typography = ({
  as: Component = 'p',
  className,
  variant = 'body-medium',
  ...props
}: TypographyProps) => {
  const variantClass = typographyVariants[variant]

  return <Component className={cn(variantClass, className)} {...props} />
}

Typography.displayName = 'Section.Typography'

const CTAButton = ({
  variant = 'primary',
  href,
  label,
  ...props
}: CTAButtonProps) => {
  const variantClass = CTAButtonVariants[variant]

  return (
    <Link href={href} {...props}>
      <button className={cn('px-12 py-3', variantClass)}>
        <Typography variant="label-button-medium">{label}</Typography>
      </button>
    </Link>
  )
}

CTAButton.displayName = 'Section.CTAButton'

export const Section = Object.assign(SectionAlone, { Typography, CTAButton })
